import { GetServerSideProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { LoggedOutContainer } from '@fe/common/components/LoggedOutContainer'
import { REFERRAL } from '@fe/common/constants/cookies'
import { COLOR, FONT_SIZE, SCREEN } from '@fe/common/constants/main'
import { getUserLocale } from '@fe/common/src/utils/nextUtils'
import { setCookie } from '@fe/common/utils/cookies'
import { LoggedOutLayout } from 'src/components/layouts/LoggedOutLayout'
import { FormStep } from 'src/components/signup/FormStep'
import { SuccessStep } from 'src/components/signup/SuccessStep'
import { isFintwin } from 'src/state/selectors/fintwinSelectors'
import { withUnauthOnly } from 'src/utils/authUtils'

const SignUpContainer = styled(LoggedOutContainer)<{ isWide: boolean }>`
  max-width: 100%;

  ${SCREEN.ABOVE_MOBILE} {
    width: ${({ isWide }) => (isWide ? 'auto' : '40rem')};
  }
`

// Sits directly under the card heading, which carries a 4rem bottom margin.
const Subtitle = styled.p`
  margin: -3rem 0 3rem;
  color: ${COLOR.GREY};
  font-size: ${FONT_SIZE.SMALL};
`

enum SignupSteps {
  Form,
  Email,
}

const Signup: NextPage = () => {
  const { t } = useTranslation('no-auth')
  const [step, setStep] = useState(SignupSteps.Form)

  const {
    query: { referral },
    replace,
  } = useRouter()

  useEffect(() => {
    if (referral) {
      setCookie(REFERRAL, referral as string, 30)
      replace('/signup', '/signup', { shallow: true })
    }
  }, [])

  const incrementStep = () => setStep(previous => previous + 1)

  const renderStep = () => {
    switch (step) {
      case SignupSteps.Form:
        return <FormStep incrementStep={incrementStep} />
      case SignupSteps.Email:
        return <SuccessStep />
    }
  }

  return (
    <LoggedOutLayout title={t('sign-up')}>
      <SignUpContainer
        heading={t('sign-up')}
        isWide={step === SignupSteps.Form}
      >
        {isFintwin() && step === SignupSteps.Form && (
          <Subtitle data-test-id="signup_subtitle">
            {t('signup-subtitle')}
          </Subtitle>
        )}
        {renderStep()}
      </SignUpContainer>
    </LoggedOutLayout>
  )
}

export const getServerSideProps: GetServerSideProps = withUnauthOnly(
  async ({ req, locale, locales }) => {
    const userLocale = getUserLocale(req, locale, locales)

    return {
      props: {
        ...(await serverSideTranslations(userLocale, [
          'common',
          'no-auth',
          'errors',
        ])),
      },
    }
  }
)

export default Signup
